import React from 'react';
import { useData } from '../../context/DataContext';
import { levelLabel } from '../../utils/calculations';

export const LODetailedTable = ({ selectedGrade, selectedSubject, onLOClick }) => {
  const { getScopedStudents, mathResults, englishResults, loMap, levelMap } = useData();

  if (loMap.size === 0) return null;

  const scoped = getScopedStudents();
  const loStats = {};

  // Accumulate correct and total counts per LO across all scoped students
  scoped.forEach(student => {
    if (!student.participated) return;
    if (selectedGrade !== 'all' && student.registeredGrade !== selectedGrade) return;

    const addSubjectStats = (subject, resultsMap) => {
      const data = resultsMap.get(student.id);
      if (data) {
        data.answers.forEach((ans, idx) => {
          if (isNaN(ans)) return;
          const studentGrade = student.registeredGrade;
          const loName = loMap.get(`${subject}-${studentGrade}-${idx + 1}`);
          
          if (loName) {
            if (!loStats[loName]) {
              loStats[loName] = {
                correct: 0,
                total: 0,
                subject: subject,
                questions: new Set(),
                levels: new Set()
              };
            }
            loStats[loName].total++;
            loStats[loName].questions.add(idx + 1);
            
            const lvl = levelMap.get(`${subject}-${studentGrade}-${idx + 1}`);
            if (lvl !== undefined && !isNaN(lvl)) {
              loStats[loName].levels.add(lvl);
            }
            
            if (ans === 1) {
              loStats[loName].correct++;
            }
          }
        });
      }
    };

    addSubjectStats('Math', mathResults);
    addSubjectStats('English', englishResults);
  });
  // Calculate percentage and sort ASCENDING (lowest scores first)
  const loList = Object.keys(loStats)
    .map(name => {
      const stats = loStats[name];
      const perc = stats.total > 0 ? (stats.correct / stats.total) * 100 : 0;
      return {
        name,
        perc,
        ...stats
      };
    })
    .sort((a, b) => a.perc - b.perc);

  const mathLOs = loList.filter(item => item.subject === 'Math');
  const englishLOs = loList.filter(item => item.subject === 'English');

  const showEnglish = selectedSubject === 'all' || selectedSubject === 'english';
  const showMath = selectedSubject === 'all' || selectedSubject === 'math';

  const renderSubjectColumn = (list, subjectName, headerBgClass, headerTextClass, bulletBg) => (
    <div className="flex flex-col">
      <h5 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: bulletBg }}>
        <span className="w-3 h-3 rounded-full" style={{ background: bulletBg }}></span> {subjectName} Learning Objectives
      </h5>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-xs border-collapse text-left">
          <thead>
            <tr className={`${headerBgClass} ${headerTextClass}`}>
              <th className="py-3 px-3 font-bold border-b">Learning Objective</th>
              <th className="py-3 px-2 font-bold border-b text-center">Qs</th>
              <th className="py-3 px-2 font-bold border-b text-center">Grade Level</th>
              <th className="py-3 px-2 font-bold border-b text-center">Score %</th>
              <th className="py-3 px-2 font-bold border-b text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan="5" className="py-8 text-center text-gray-400 italic">
                  No learning objective data matches these criteria.
                </td>
              </tr>
            ) : (
              list.map((item, idx) => {
                const isProficient = item.perc >= 80;
                const qNumbers = Array.from(item.questions).sort((a, b) => a - b).map(q => `Q${q}`).join(', ');
                const gradeLevels = Array.from(item.levels).sort((a, b) => a - b).map(levelLabel).join(', ') || '—';

                return (
                  <tr
                    key={idx}
                    onClick={() => onLOClick(item.name, item.subject)}
                    className="border-b hover:bg-gray-50 cursor-pointer font-medium"
                  >
                    <td className="py-2.5 px-3 font-semibold text-gray-800" title={item.name}>
                      {item.name}
                    </td>
                    <td className="py-2.5 px-2 text-center text-gray-600 font-bold">{qNumbers}</td>
                    <td className="py-2.5 px-2 text-center text-gray-600 font-bold">{gradeLevels}</td>
                    <td
                      className="py-2.5 px-2 text-center font-bold"
                      style={{ color: isProficient ? '#16a34a' : '#dc2626' }}
                    >
                      {item.perc.toFixed(1)}%
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <span
                        className="px-2 py-0.5 rounded text-[10px] font-bold text-white shadow-xs"
                        style={{ backgroundColor: isProficient ? '#16a34a' : '#ef4444' }}
                      >
                        {isProficient ? 'Prof' : 'Needs Focus'}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
  return (
    <div className="grid grid-cols-1 gap-8">
      {showEnglish &&
        renderSubjectColumn(englishLOs, 'English', 'bg-green-50', 'text-green-800', '#16a34a')}
      {showMath &&
        renderSubjectColumn(mathLOs, 'Mathematics', 'bg-blue-50', 'text-blue-800', '#2563eb')}
    </div>
  );
};
export default LODetailedTable;
